/**
 * useNotebookRouting (Intelligence Studio)
 *
 * SRP hook: navigation + URL sync for notebook selection, plus a safe notebook update bridge.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 */

import { useCallback, useRef } from 'react';

import { applyNotebookPatch, mergeFilesReconciled } from '@/pages/intelligence-studio/utils/notebookEventReconciler';

export default function useNotebookRouting({
  selectedNotebook,
  setSelectedNotebook,
  setView,
  setSearchParams,
  setNotebooks,
}) {
  // ✅ PHASE 5: Event reconciliation (dedup + stale discard)
  const lastAppliedNotebookUpdateAtRef = useRef(new Map()); // notebookId -> updatedAt(ms)

  const mergeFiles = (prevFiles = [], patchFiles = []) => mergeFilesReconciled(prevFiles, patchFiles);

  // Handle selecting a notebook (switch to notebook view)
  const handleSelectNotebook = notebook => {
    setSelectedNotebook(notebook);
    setView('notebook');
    // Update URL with notebook ID for persistence on refresh
    setSearchParams({ id: notebook.id });
  };

  // Handle going back to home
  const handleBackToHome = () => {
    setSelectedNotebook(null);
    setView('home');
    // Clear notebook ID from URL
    setSearchParams({});
  };

  // ✅ Notebook update bridge (kept here so views remain presentational-only)
  // IMPORTANT: MainPanel emits partial updates from SSE (status/progress), so we must MERGE.
  const handleNotebookUpdate = useCallback(
    patch => {
      if (!patch) return;

      const targetId = patch?.id || selectedNotebook?.id;
      if (!targetId) return;

      // Stale discard at notebook level (updatedAt regression)
      try {
        const prevLast = lastAppliedNotebookUpdateAtRef.current.get(targetId) || 0;
        const patchUpdatedAt = Date.parse(patch?.updatedAt || '') || 0;
        if (patchUpdatedAt && prevLast && patchUpdatedAt < prevLast) {
          return;
        }
        if (patchUpdatedAt) lastAppliedNotebookUpdateAtRef.current.set(targetId, patchUpdatedAt);
      } catch {}

      // Merge into selected notebook
      setSelectedNotebook(prev => {
        if (!prev) return prev;
        // If we're updating a different notebook than the one selected, ignore
        const prevId = prev?.id;
        if (prevId && prevId !== targetId) return prev;
        return applyNotebookPatch(prev, { ...patch, id: targetId });
      });

      // Merge into notebooks list
      setNotebooks(prev =>
        prev.map(n => {
          if (n.id !== targetId) return n;
          return applyNotebookPatch(n, { ...patch, id: targetId });
        })
      );
    },
    [selectedNotebook?.id, setNotebooks, setSelectedNotebook]
  );

  return {
    handleSelectNotebook,
    handleBackToHome,
    handleNotebookUpdate,
  };
}
